// Single source of truth for the app shell navigation (sidebar, mobile
// bottom bar, page titles) and for where each role lands after login.
// `bottom: true` items appear in the mobile bottom bar; `short` is the label
// used there. An item with `children` is a collapsible group of links in the
// sidebar. `superAdminOnly` / `hideForSuperAdmin` show an item only to, or to
// everyone but, the super admin. Role keys match the `meta.roles` used in
// router.js.

import { USER_PAGES, DUPLICATES_ROUTE } from "../superAdmin/userRoles";

// uType codes come from Backend/config/userType.js. 771 is an older division
// code the backend still treats as a DFO.
export function roleForUType(uType) {
  const code = String(uType == null ? "" : uType).trim();
  if (code === "780") return "superadmin";
  if (code === "777") return "admin";
  if (code === "772") return "user";
  if (code === "773") return "range";
  if (code === "7741") return "clerk";
  if (code === "7742") return "superintendent";
  return "division";
}

export function getUserType() {
  return roleForUType(localStorage.getItem("uType"));
}

export function isSuperAdmin() {
  return localStorage.getItem("uType") === "780";
}

// Roles that see a full application list may bulk delete from it: admins (777),
// super admins (780) and DFOs (774, and the older 771), who only get their own
// division's applications (the server enforces that).
export function canBulkDeleteApps() {
  return ["777", "780", "774", "771"].includes(String(localStorage.getItem("uType")));
}

export const panelLabels = {
  superadmin: "Super Admin",
  admin: "Admin Panel",
  division: "Division Panel",
  clerk: "Section Clerk",
  superintendent: "Superintendent",
  range: "Range Officer",
  user: "Film Shooting Permit",
};

// Landing page for each role after login (and when an authenticated user
// opens /login).
export const homeRoutes = {
  superadmin: "/admin-dashboard",
  admin: "/admin-dashboard",
  division: "/divisiondashboard",
  clerk: "/ClerkDashboard",
  superintendent: "/SuperintendentDashboard",
  range: "/RangeDashboard",
  user: "/mybooking",
};

export function homeRouteFor(uType) {
  return homeRoutes[roleForUType(uType)];
}

const adminMenu = [
  { section: "Overview", name: "Dashboard", short: "Home", route: "/admin-dashboard", icon: "mdi-view-dashboard-outline", bottom: true },
  { section: "Overview", name: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline" },
  { section: "Applications", name: "All Applications", short: "Apps", route: "/admin-applications", icon: "mdi-file-document-multiple-outline", bottom: true },
  { section: "Applications", name: "Track Applications", short: "Track", route: "/track-applications", icon: "mdi-map-marker-path", bottom: true },
  { section: "Payments", name: "Completed Payments", short: "Payments", route: "/admin-payments", icon: "mdi-cash-check", bottom: true },
  { section: "Payments", name: "eTreasury Search", route: "/etreasury-search", icon: "mdi-bank-outline" },
  { section: "Payments", name: "Payment Log", route: "/payment-log", icon: "mdi-clipboard-text-clock-outline", superAdminOnly: true },
  { section: "Payments", name: "Namudekeralam Applications", route: "/namudekeralam-applications", icon: "mdi-cash-check", superAdminOnly: true },
  {
    section: "Users & Access",
    name: "Users List",
    icon: "mdi-account-group-outline",
    superAdminOnly: true,
    children: USER_PAGES.map((p) => ({ name: p.name, route: p.route, icon: p.icon })),
  },
  { section: "Users & Access", name: "Duplicate Accounts", route: DUPLICATES_ROUTE, icon: "mdi-account-multiple-alert-outline", superAdminOnly: true },
  // The super admin manages these accounts on the Users List role pages
  { section: "Users & Access", name: "Range Officers", route: "/range-officers", icon: "mdi-account-hard-hat-outline", hideForSuperAdmin: true },
  { section: "Users & Access", name: "Division Officers", route: "/division-officers", icon: "mdi-account-tie-outline", hideForSuperAdmin: true },
  { section: "Users & Access", name: "E-Treasury Office Code", route: "/division-code", icon: "mdi-pound-box-outline" },
];

const menus = {
  superadmin: adminMenu,
  admin: adminMenu,
  division: [
    { section: "Overview", name: "Dashboard", short: "Home", route: "/divisiondashboard", icon: "mdi-view-dashboard-outline", bottom: true },
    { section: "Overview", name: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline" },
    { section: "Applications", name: "Application List", short: "List", route: "/ApplicationDivision", icon: "mdi-format-list-bulleted-square", bottom: true },
    // The full division list (/division-applications) is opened from the
    // dashboard's count cards rather than the menu.
    { section: "Applications", name: "Cancel Requests", route: "/cancelrequests", icon: "mdi-cancel" },
    { section: "Applications", name: "Refunded Applications", route: "/RefundedApplications", icon: "mdi-cash-refund" },
    { section: "Applications", name: "Track Applications", route: "/track-applications", icon: "mdi-map-marker-path" },
    { section: "Payments", name: "Completed Payments", short: "Payments", route: "/admin-payments", icon: "mdi-cash-check", bottom: true },
    { section: "Payments", name: "eTreasury Search", route: "/etreasury-search", icon: "mdi-bank-outline" },
    { section: "Staff", name: "Employees", route: "/employees", icon: "mdi-account-multiple-plus-outline" },
    // { section: "Staff", name: "E-Treasury Office Code", route: "/division-code", icon: "mdi-pound-box-outline" },
  ],
  clerk: [
    { name: "Dashboard", short: "Home", route: "/ClerkDashboard", icon: "mdi-view-dashboard-outline", bottom: true },
    { name: "Application List", short: "List", route: "/sectionhome", icon: "mdi-format-list-bulleted-square", bottom: true },
    { name: "Profile", short: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline", bottom: true },
  ],
  superintendent: [
    { name: "Dashboard", short: "Home", route: "/SuperintendentDashboard", icon: "mdi-view-dashboard-outline", bottom: true },
    { name: "Application List", short: "List", route: "/superintedent", icon: "mdi-format-list-bulleted-square", bottom: true },
    { name: "Profile", short: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline", bottom: true },
  ],
  range: [
    { name: "Dashboard", short: "Home", route: "/RangeDashboard", icon: "mdi-view-dashboard-outline", bottom: true },
    { name: "Application List", short: "List", route: "/rangebookings", icon: "mdi-format-list-bulleted-square", bottom: true },
    { name: "Profile", short: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline", bottom: true },
  ],
  user: [
    { name: "New Application", short: "Apply", route: "/booknow", icon: "mdi-file-plus-outline", bottom: true },
    { name: "My Applications", short: "My Apps", route: "/mybooking", icon: "mdi-file-document-outline", bottom: true },
    { name: "Licence Issued", short: "Licence", route: "/licenceissued", icon: "mdi-file-certificate-outline" },
    { name: "Returned to Me", short: "Returned", route: "/ForwardedApplications", icon: "mdi-reply-outline", bottom: true },
    { name: "Rejected Applications", short: "Rejected", route: "/rejectedapplications", icon: "mdi-close-octagon-outline" },
    { name: "Cancelled Applications", short: "Cancelled", route: "/cancelledapplications", icon: "mdi-close-circle-outline", bottom: true },
    { name: "Track Application", short: "Track", route: "/trackapplication", icon: "mdi-map-marker-path" },
    { name: "Profile", route: "/profilepage", icon: "mdi-account-circle-outline" },
  ],
};

export function getNavItems(userType) {
  const items = menus[userType] || menus.division;
  if (isSuperAdmin()) return items.filter((item) => !item.hideForSuperAdmin);
  return items.filter((item) => !item.superAdminOnly);
}
